import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Package,
  Download,
  ArrowLeft,
  ArrowRight,
  GitBranch,
  GitCommit,
  AlertCircle,
  CheckCircle2,
  GitCompare,
} from 'lucide-react';
import toast from '../../design-system/toast';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import type { SbomListEntry, SbomListResponse } from '../../types/sbom';
import {
  SEVERITY_META,
  SEVERITY_ORDER,
  SeverityPills,
  type SeverityKey,
} from '../../components/findings/severityDisplay';

const SummaryMetric: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="p-4 sm:p-5">
    <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">{label}</div>
    <div className="font-mono text-2xl font-bold text-ui-text-primary mt-1">{value}</div>
  </div>
);

const SeverityDistributionBar: React.FC<{ summary: Record<SeverityKey, number>; total: number }> = ({
  summary,
  total,
}) => {
  if (total === 0) {
    return <div className="h-1.5 rounded-full bg-semantic-success/20" />;
  }

  return (
    <div className="h-1.5 rounded-full bg-ui-background-tertiary overflow-hidden flex">
      {SEVERITY_ORDER.map((sev) => {
        const count = summary[sev] || 0;
        if (!count) return null;
        return (
          <div
            key={sev}
            className={`h-full ${SEVERITY_META[sev].bar}`}
            style={{ flexBasis: 0, flexGrow: count, minWidth: 2 }}
            title={`${count} ${SEVERITY_META[sev].label}`}
          />
        );
      })}
    </div>
  );
};

const SeveritySummaryChip: React.FC<{ severity: SeverityKey; count: number }> = ({ severity, count }) => {
  const meta = SEVERITY_META[severity];
  const Icon = meta.icon;
  const activeClasses = `${meta.bg} ${meta.text} ${meta.border}`;
  const inactiveClasses = 'bg-ui-background-tertiary text-ui-text-tertiary border-ui-border-secondary';

  return (
    <span
      className={`inline-flex min-w-[6.25rem] items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs font-medium ${
        count > 0 ? activeClasses : inactiveClasses
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        {meta.label}
      </span>
      <span className="font-mono font-semibold">{count}</span>
    </span>
  );
};

const SbomSummaryBand: React.FC<{
  sbomCount: number;
  componentCount: number;
  vulnerabilitySummary: Record<SeverityKey, number>;
}> = ({ sbomCount, componentCount, vulnerabilitySummary }) => {
  const vulnerabilityTotal = SEVERITY_ORDER.reduce((acc, sev) => acc + vulnerabilitySummary[sev], 0);
  const highestSeverity = SEVERITY_ORDER.find((sev) => vulnerabilitySummary[sev] > 0);
  const highestMeta = highestSeverity ? SEVERITY_META[highestSeverity] : null;
  const StatusIcon = highestMeta?.icon || CheckCircle2;

  return (
    <div className="mb-6 overflow-hidden rounded-lg border border-ui-border-primary bg-ui-background-secondary">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
        <div className="grid grid-cols-2 divide-x divide-ui-border-primary border-b border-ui-border-primary lg:border-b-0 lg:border-r">
          <SummaryMetric label="SBOMs" value={sbomCount} />
          <SummaryMetric label="Components" value={componentCount.toLocaleString()} />
        </div>

        <div className="p-4 sm:p-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
                    highestMeta ? highestMeta.bg : 'bg-semantic-success/10'
                  }`}
                >
                  <StatusIcon
                    className={`h-5 w-5 ${highestMeta ? highestMeta.text : 'text-semantic-success'}`}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Vulnerability exposure
                  </div>
                  <div className="text-sm font-semibold text-ui-text-primary">
                    {highestMeta
                      ? `${highestMeta.label} is the highest active severity`
                      : 'No matched vulnerabilities'}
                  </div>
                </div>
              </div>

              <div className="sm:text-right">
                <div className="font-mono text-2xl font-bold text-ui-text-primary">
                  {vulnerabilityTotal.toLocaleString()}
                </div>
                <div className="text-xs text-ui-text-tertiary">Total vulnerabilities</div>
              </div>
            </div>

            <SeverityDistributionBar summary={vulnerabilitySummary} total={vulnerabilityTotal} />

            <div className="flex flex-wrap gap-2">
              {SEVERITY_ORDER.map((sev) => (
                <SeveritySummaryChip key={sev} severity={sev} count={vulnerabilitySummary[sev]} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LicenseChips: React.FC<{ summary: Record<string, number> }> = ({ summary }) => {
  const entries = Object.entries(summary)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);
  const remaining = Object.keys(summary).length - entries.length;
  if (!entries.length) {
    return <span className="text-xs text-ui-text-tertiary">No license data</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([id, count]) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-ui-background-tertiary text-ui-text-secondary border border-ui-border-secondary"
        >
          <span className="font-mono">{id}</span>
          <span className="text-ui-text-tertiary">·</span>
          <span>{count}</span>
        </span>
      ))}
      {remaining > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-ui-text-tertiary">
          +{remaining} more
        </span>
      )}
    </div>
  );
};

const SbomListPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const api = useAuthenticatedApi();
  const [sboms, setSboms] = useState<SbomListEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSboms = useCallback(async () => {
    if (!applicationId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<SbomListResponse>(
        apiEndpoints.sboms.listForApplication(applicationId, 50, 0),
      );
      setSboms(response.sboms);
    } catch (err: any) {
      setError(err?.message || 'Failed to load SBOMs');
    } finally {
      setIsLoading(false);
    }
  }, [api, applicationId]);

  useEffect(() => {
    loadSboms();
  }, [loadSboms]);

  const handleDownload = async (sbomId: string) => {
    try {
      const blob = await api.getBlob(apiEndpoints.sboms.download(sbomId));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sbom-${sbomId}.cyclonedx.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download SBOM');
    }
  };

  const totals = useMemo(() => {
    const vulnerabilitySummary: Record<SeverityKey, number> = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
    let components = 0;
    sboms.forEach((s) => {
      components += s.component_count;
      SEVERITY_ORDER.forEach((sev) => {
        vulnerabilitySummary[sev] += s.vulnerability_summary[sev] || 0;
      });
    });
    return { components, vulnerabilitySummary };
  }, [sboms]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size={40} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading SBOMs…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate(`/repositories/${applicationId}`)}
          className="flex items-center gap-2 text-sm text-ui-text-secondary hover:text-ui-text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to application
        </button>

        <div className="flex items-start gap-3 sm:gap-4 mb-6">
          <div className="w-12 h-12 bg-ui-background-secondary border border-ui-border-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-brand-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-ui-text-primary">Software Bill of Materials</h1>
            <p className="text-sm text-ui-text-secondary mt-1">
              Component inventories produced by SBOM scans, with vulnerability and license breakdowns.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-semantic-error/10 border border-semantic-error/20 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-semantic-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-semantic-error mb-1">Failed to load SBOMs</p>
                <p className="text-sm text-semantic-error">{error}</p>
              </div>
            </div>
          </div>
        )}

        {sboms.length === 0 && !error ? (
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-ui-background-tertiary rounded-full flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-ui-text-tertiary" />
              </div>
              <h3 className="text-lg font-semibold text-ui-text-primary mb-2">No SBOMs yet</h3>
              <p className="text-sm text-ui-text-secondary mb-6 max-w-md">
                Run an SBOM scan to produce a component inventory and match CVEs against it. Re-run scans to see
                drift between versions.
              </p>
            </div>
          </div>
        ) : (
          sboms.length > 0 && (
            <>
              <SbomSummaryBand
                sbomCount={sboms.length}
                componentCount={totals.components}
                vulnerabilitySummary={totals.vulnerabilitySummary}
              />

              <div className="space-y-3">
                {sboms.map((sbom) => (
                  <div
                    key={sbom.id}
                    className="bg-ui-background-secondary border border-ui-border-primary rounded-xl hover:border-brand-primary/50 transition-colors"
                  >
                    <div className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/repositories/${applicationId}/sbom/${sbom.id}`}
                            className="font-semibold text-ui-text-primary hover:text-brand-primary transition-colors font-mono text-sm break-all"
                          >
                            {sbom.repository_full_name}
                          </Link>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-ui-text-tertiary">
                            {sbom.branch && (
                              <span className="inline-flex items-center gap-1">
                                <GitBranch className="w-3 h-3" />
                                <span className="font-mono">{sbom.branch}</span>
                              </span>
                            )}
                            {sbom.git_ref && (
                              <span className="inline-flex items-center gap-1">
                                <GitCommit className="w-3 h-3" />
                                <span className="font-mono">{sbom.git_ref.slice(0, 8)}</span>
                              </span>
                            )}
                            <span>{new Date(sbom.created_at).toLocaleString()}</span>
                            {sbom.has_diff && (
                              <span className="inline-flex items-center gap-1 text-semantic-info">
                                <GitCompare className="w-3 h-3" />
                                Drift recorded
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleDownload(sbom.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ui-text-secondary hover:text-ui-text-primary bg-ui-background-tertiary border border-ui-border-secondary rounded-lg transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            CycloneDX
                          </button>
                          <Link
                            to={`/repositories/${applicationId}/sbom/${sbom.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-brand-primary hover:bg-brand-primaryDark text-black rounded-lg transition-colors"
                          >
                            Open
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-ui-border-primary">
                        <div>
                          <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider mb-1.5">
                            Components
                          </div>
                          <div className="text-lg font-semibold text-ui-text-primary">
                            {sbom.component_count.toLocaleString()}
                          </div>
                        </div>
                        <div className="md:col-span-1">
                          <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider mb-1.5">
                            Vulnerabilities
                          </div>
                          <SeverityPills summary={sbom.vulnerability_summary} />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider mb-1.5">
                            Top licenses
                          </div>
                          <LicenseChips summary={sbom.license_summary} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default SbomListPage;
